import type { UseFormReturn } from "react-hook-form";

// ─── Types ──────────────────────────────────────────

export interface OnlineOrderingFormValues {
  slug: string;
  isLive: boolean;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  curbsideEnabled: boolean;
  defaultPrepTimeMinutes: number;
  deliveryTimeMinutes: number;
  scheduleSlotIntervalMinutes: number;
  futureOrdersEnabled: boolean;
  futureOrderMaxDays: number;
  autoAcceptOrders: boolean;
  autoAcceptDelaySeconds: number;
  tipOptions: number[];
  defaultTipPercent: number;
  minimumOrderAmount: number;
  deliveryMinimumAmount: number;
  deliveryFee: number;
  logoStorageId: string | null;
  bannerImageUrl: string | null;
  bannerStorageId: string | null;
  faviconStorageId: string | null;
  ogImageStorageId: string | null;
  accentColor: string;
  secondaryColor: string;
  fontFamily: string;
  welcomeMessage: string;
  maxPickupOrdersPerSlot: number;
  maxDeliveryOrdersPerSlot: number;
  maxCurbsideOrdersPerSlot: number;
  isPaused: boolean;
  pauseMessage: string;
  pauseUntil: string;
  useCustomOnlineHours: boolean;
  onlineOrderingHours: Array<{
    dayOfWeek: string;
    isOpen: boolean;
    openTime: string;
    closeTime: string;
  }>;
  scheduleOverrides: Array<{
    date: string;
    type: "closed" | "modified";
    openTime: string;
    closeTime: string;
    reason: string;
  }>;
}

export const DEFAULT_VALUES: OnlineOrderingFormValues = {
  slug: "",
  isLive: false,
  pickupEnabled: true,
  deliveryEnabled: false,
  curbsideEnabled: false,
  defaultPrepTimeMinutes: 15,
  deliveryTimeMinutes: 30,
  scheduleSlotIntervalMinutes: 30,
  futureOrdersEnabled: false,
  futureOrderMaxDays: 7,
  autoAcceptOrders: false,
  autoAcceptDelaySeconds: 0,
  tipOptions: [15, 18, 20],
  defaultTipPercent: 18,
  minimumOrderAmount: 0,
  deliveryMinimumAmount: 0,
  deliveryFee: 0,
  logoStorageId: null,
  bannerImageUrl: null,
  bannerStorageId: null,
  faviconStorageId: null,
  ogImageStorageId: null,
  accentColor: "#000000",
  secondaryColor: "",
  fontFamily: "",
  welcomeMessage: "",
  maxPickupOrdersPerSlot: 0,
  maxDeliveryOrdersPerSlot: 0,
  maxCurbsideOrdersPerSlot: 0,
  isPaused: false,
  pauseMessage: "",
  pauseUntil: "",
  useCustomOnlineHours: false,
  onlineOrderingHours: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ].map((day) => ({
    dayOfWeek: day,
    isOpen: false,
    openTime: "09:00",
    closeTime: "21:00",
  })),
  scheduleOverrides: [],
};

export interface SettingsInput {
  slug: string;
  isLive: boolean;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  curbsideEnabled: boolean;
  defaultPrepTimeMinutes?: number;
  deliveryTimeMinutes?: number;
  scheduleSlotIntervalMinutes?: number;
  futureOrdersEnabled?: boolean;
  futureOrderMaxDays?: number;
  autoAcceptOrders?: boolean;
  autoAcceptDelaySeconds?: number;
  tipOptions?: number[];
  defaultTipPercent?: number;
  minimumOrderAmount?: number;
  deliveryMinimumAmount?: number;
  deliveryFee?: number;
  logoStorageId?: string;
  bannerImageUrl?: string;
  bannerStorageId?: string;
  faviconStorageId?: string;
  ogImageStorageId?: string;
  accentColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  welcomeMessage?: string;
  maxPickupOrdersPerSlot?: number;
  maxDeliveryOrdersPerSlot?: number;
  maxCurbsideOrdersPerSlot?: number;
  isPaused?: boolean;
  pauseMessage?: string;
  pauseUntil?: number;
  onlineOrderingHours?: Array<{
    dayOfWeek: string;
    isOpen: boolean;
    openTime?: string;
    closeTime?: string;
  }>;
  scheduleOverrides?: Array<{
    date: string;
    type: "closed" | "modified";
    openTime?: string;
    closeTime?: string;
    reason?: string;
  }>;
}

export interface SectionProps {
  form: UseFormReturn<OnlineOrderingFormValues>;
  onUpdate: () => void;
}

export const FONT_OPTIONS = [
  { label: "System Default", value: "" },
  { label: "Inter", value: "Inter" },
  { label: "DM Sans", value: "DM Sans" },
  { label: "Lora", value: "Lora" },
  { label: "Playfair Display", value: "Playfair Display" },
  { label: "Nunito", value: "Nunito" },
  { label: "Poppins", value: "Poppins" },
  { label: "Montserrat", value: "Montserrat" },
  { label: "Open Sans", value: "Open Sans" },
  { label: "Roboto Slab", value: "Roboto Slab" },
] as const;
